import React from 'react';
import { ArrowLeft, BookOpen, Users, Target, Lightbulb, Award, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const ModeloAcademico = () => {
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-16">
                <div className="max-w-7xl mx-auto px-6">
                    <Link
                        to="/maestria"
                        className="inline-flex items-center gap-2 text-green-200 hover:text-white mb-6 transition-colors duration-300"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Volver a Maestría
                    </Link>
                    <div className="flex items-center gap-4">
                        <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
                            <BookOpen className="w-12 h-12" />
                        </div>
                        <div>
                            <h1 className="text-4xl md:text-5xl font-bold mb-2">Modelo Académico</h1>
                            <p className="text-xl text-green-100">
                                Metodología pedagógica del CENID
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-16">
                {/* Introducción */}
                <section className="mb-16">
                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                        <div className="flex items-center gap-3 mb-8">
                            <BookOpen className="w-8 h-8 text-[#0b742f]" />
                            <h2 className="text-3xl font-bold text-gray-900">Modelo Académico</h2>
                        </div>
                        <div className="prose prose-lg max-w-none text-gray-700">
                            <p className="text-lg leading-relaxed mb-6">
                                A continuación describimos a detalle el modelo pedagógico que el CENID utiliza 
                                en sus programas por Internet
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mt-8">
                            <div className="bg-blue-50 p-4 rounded-lg text-center">
                                <h3 className="font-semibold text-blue-800">• Constructivismo</h3>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg text-center">
                                <h3 className="font-semibold text-green-800">• Metodología</h3>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg text-center">
                                <h3 className="font-semibold text-purple-800">• Alumno</h3>
                            </div>
                            <div className="bg-orange-50 p-4 rounded-lg text-center">
                                <h3 className="font-semibold text-orange-800">• Profesor - Asesor</h3>
                            </div>
                            <div className="bg-red-50 p-4 rounded-lg text-center">
                                <h3 className="font-semibold text-red-800">• Tecnología</h3>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Constructivismo */}
                <section className="mb-16">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-xl p-8 md:p-12 border border-blue-100">
                        <div className="flex items-center gap-3 mb-8">
                            <Lightbulb className="w-10 h-10 text-blue-600" />
                            <h2 className="text-3xl font-bold text-gray-900">Constructivismo</h2>
                        </div>
                        
                        <p className="text-lg leading-relaxed text-gray-700 mb-8">
                            Al hablar de constructivismo se hace mención a un conjunto de nociones teóricas, 
                            concepciones, interpretaciones y prácticas que sostienen la afirmación de que 
                            el conocimiento no es el resultado de una mera copia de la realidad preexistente, 
                            sino de un proceso dinámico e interactivo a través del cual la información 
                            externa es interpretada y re-interpretada por la mente que va construyendo 
                            progresivamente modelos explicativos cada vez más complejos y potentes (La Casa, 2014). 
                            En otras palabras, conocemos la realidad a través de los modelos que construimos 
                            para explicarla, y estos modelos son susceptibles de ser mejorados o modificados.
                        </p>

                        <div className="space-y-8">
                            <div className="bg-white rounded-lg p-6">
                                <h3 className="text-xl font-bold text-blue-700 mb-4">Aprendizaje</h3>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    El aprendizaje es entendido como un cambio en la disposición o en la conducta de 
                                    un organismo, cambio que suele ser permanente y que no es producto de un solo 
                                    proceso de crecimiento (Delval, 2013). Así, el aprendizaje solo se da cuando 
                                    existan las estructuras necesarias para que el sujeto dé una nueva respuesta; la 
                                    formación de las estructuras es producto del proceso de desarrollo.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    Para Piaget, el término constructivismo implica que el sujeto construye 
                                    activamente su comprensión del mundo. Por otra parte, Vigotsky (1919) señala la 
                                    influencia e importancia de los hechos sociales y considera como fundamentales a las 
                                    personas que rodean al sujeto que aprende y se desarrolla.
                                </p>
                                <p className="text-gray-700 leading-relaxed">
                                    Además, en el ámbito constructivista, se hace énfasis en buscar lo que se conoce 
                                    como aprendizaje significativo.
                                </p>
                            </div>

                            <div className="bg-white rounded-lg p-6">
                                <h3 className="text-xl font-bold text-blue-700 mb-4">Aprendizaje significativo</h3>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    Según David Jonassen (1999), éste ocurre cuando los estudiantes están creando 
                                    activamente significado; es decir, de forma activa (manipulando y/o observando), 
                                    constructiva (articulando y reflexionando), intencional (regulando y 
                                    reflexionando), auténtica (de manera compleja y contextualizada) y colaborativa (de forma 
                                    dialogada).
                                </p>
                                <p className="text-gray-700 leading-relaxed">
                                    Aprendemos de experimentar fenómenos (objetos, eventos, actividades, procesos) 
                                    interpretando aquellas experiencias basadas en lo que ya conocemos, razonando 
                                    sobre ellas y reflexionando sobre dichas experiencias y el razonamiento mismo. Este 
                                    proceso, que Brunner (1990) llamaba "creación del significado", es el corazón y 
                                    la esencia del constructivismo.
                                </p>
                            </div>

                            <div className="bg-white rounded-lg p-6">
                                <h3 className="text-xl font-bold text-blue-700 mb-4">Aprendizaje colaborativo</h3>
                                <p className="text-gray-700 leading-relaxed">
                                    Por otra parte, la cooperación es una estructura de interacción diseñada para 
                                    facilitar el logro de una meta o producto final específico por un grupo de 
                                    personas que trabajan juntas. El aprendizaje colaborativo es centrado en el estudiante 
                                    (Panitz 1997, 1998).
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Metodología */}
                <section className="mb-16">
                    <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12">
                        <div className="flex items-center gap-3 mb-8">
                            <Target className="w-10 h-10 text-[#e87729]" />
                            <h2 className="text-3xl font-bold">Metodología</h2>
                        </div>
                        
                        <p className="text-lg leading-relaxed text-white/95 mb-6">
                            Para poder poner en práctica los conceptos antes mencionados, el CENID, ha diseñado 
                            una metodología que permite desarrollar ambientes de aprendizaje acordes a la necesidad 
                            de nuestro marco conceptual. Dicha metodología es conocida como "Enfoque de búsqueda", 
                            la cual es una descripción general del desempeño de tipo profesional que el estudiante 
                            será capaz de realizar después de llevar a cabo todas las tareas del curso.
                        </p>

                        <p className="text-lg leading-relaxed text-white/95 mb-6">
                            La idea básica es que el estudiante aprenda viviendo una situación simulada que lo 
                            lleve a pensar como parte de una comunidad profesional dedicada a esa área de estudio. 
                            Así se tiene un aprendizaje situado o contextualizado, más apegado a una realidad que 
                            a una clase teórica impartida a través de un pizarrón.
                        </p>

                        <p className="text-lg leading-relaxed text-white/95 mb-6">
                            Se espera que el objetivo del modelo se desarrolle por medio de la realización de un 
                            conjunto de actividades llamadas centradas en el autoaprendizaje que bien pueden ser:
                        </p>

                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-6">
                            <ul className="space-y-3 text-white">
                                <li className="flex items-center">
                                    <span className="w-2 h-2 bg-[#e87729] rounded-full mr-3"></span>
                                    Solucionar un problema
                                </li>
                                <li className="flex items-center">
                                    <span className="w-2 h-2 bg-[#e87729] rounded-full mr-3"></span>
                                    Tomar una decisión
                                </li>
                                <li className="flex items-center">
                                    <span className="w-2 h-2 bg-[#e87729] rounded-full mr-3"></span>
                                    Realizar un diseño
                                </li>
                                <li className="flex items-center">
                                    <span className="w-2 h-2 bg-[#e87729] rounded-full mr-3"></span>
                                    Emitir un juicio
                                </li>
                            </ul>
                        </div>

                        <p className="text-lg leading-relaxed text-white/95">
                            En una palabra, lo que queremos es que nuestros alumnos realmente piensen y se den cuenta 
                            por sí mismos de qué manera van logrando construir su propio conocimiento en el preciso 
                            momento en que ellos deseen. Esto, sin lugar a dudas representa una ventaja competitiva 
                            para los estudiantes participantes en esta modalidad de estudio.
                        </p>
                    </div>
                </section>

                {/* Alumno */}
                <section className="mb-16">
                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-purple-500">
                        <div className="flex items-center gap-3 mb-8">
                            <Users className="w-10 h-10 text-purple-600" />
                            <h2 className="text-3xl font-bold text-gray-900">Alumno</h2>
                        </div>
                        
                        <p className="text-lg leading-relaxed text-gray-700 mb-6">
                            Por parte de los alumnos, se requiere también de un cambio en el paradigma de su rol. 
                            No deben de ser un elemento pasivo del proceso enseñanza-aprendizaje, sino al contrario, 
                            deben ser individuos activos, con gran respeto hacia sí mismos, con alta autoestima y 
                            confianza en sus habilidades y capacidades.
                        </p>

                        <p className="text-lg leading-relaxed text-gray-700 mb-6">
                            Deben de ser autorregulados, con un alto grado de autonomía en la búsqueda y construcción 
                            de su conocimiento. La autorregulación de los estudiantes se hará presente en la 
                            planificación de sus actividades y la monitorización de sus acciones, a partir de los 
                            resultados intermedios que vayan consiguiendo.
                        </p>

                        <p className="text-lg leading-relaxed text-gray-700">
                            Los estudiantes deberán tener una fuerte autodisciplina para el trabajo y una gran 
                            orientación al establecimiento y logro de objetivos.
                        </p>
                    </div>
                </section>

                {/* Profesor-Asesor */}
                <section className="mb-16">
                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#e87729]">
                        <div className="flex items-center gap-3 mb-8">
                            <Award className="w-10 h-10 text-[#e87729]" />
                            <h2 className="text-3xl font-bold text-gray-900">Profesor – Asesor</h2>
                        </div>
                        
                        <div className="space-y-8">
                            <div className="bg-orange-50 rounded-lg p-6">
                                <h3 className="text-xl font-bold text-[#e87729] mb-4">Conocimientos - Cognitivo</h3>
                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        Es un experto técnico, conoce en profundidad y domina los contenidos de su 
                                        materia y del área aplicativa donde se desarrollan cotidianamente los 
                                        intercambios teoría-práctica.
                                    </p>
                                    <p className="text-gray-700 leading-relaxed">
                                        Identifica los componentes centrales de su curso en modalidad online, los 
                                        contextualiza y propicia aprendizajes sociales teniendo en cuenta las 
                                        necesidades y características de sus estudiantes y del contexto.
                                    </p>
                                </div>
                            </div>

                            <div className="bg-orange-50 rounded-lg p-6">
                                <h3 className="text-xl font-bold text-[#e87729] mb-4">Habilidades - Aptitudes</h3>
                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        Domina la plataforma educativa, atendiendo en tiempo y forma a las dudas e 
                                        inquietudes de los estudiantes.
                                    </p>
                                    <p className="text-gray-700 leading-relaxed">
                                        Diseña experiencias de aprendizaje con claridad y precisión, integrando 
                                        diferentes estrategias didácticas y pedagógicas respondiendo a las 
                                        características del participante y del curso.
                                    </p>
                                    <p className="text-gray-700 leading-relaxed">
                                        Orienta a los estudiantes ante la aparición de problemas de tipo pedagógico, 
                                        técnico o administrativo.
                                    </p>
                                    <p className="text-gray-700 leading-relaxed">
                                        Estimula un aprendizaje de pares e investigación en la red.
                                    </p>
                                </div>
                            </div>

                            <div className="bg-orange-50 rounded-lg p-6">
                                <h3 className="text-xl font-bold text-[#e87729] mb-4">Actitudes</h3>
                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        Aborda la producción de los contenidos con base en las necesidades y 
                                        características de los estudiantes y su contexto.
                                    </p>
                                    <p className="text-gray-700 leading-relaxed">
                                        Motiva permanentemente el aprendizaje y el logro de los objetivos formativos 
                                        propuestos para su curso.
                                    </p>
                                    <p className="text-gray-700 leading-relaxed">
                                        Asume su compromiso como facilitador, respetando el ritmo y autonomía del 
                                        estudiante y los criterios institucionales respecto al proceso de aprendizaje.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Tecnologías */}
                <section>
                    <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl shadow-xl p-8 md:p-12 border border-red-100">
                        <div className="flex items-center gap-3 mb-8">
                            <Target className="w-10 h-10 text-red-600" />
                            <h2 className="text-3xl font-bold text-gray-900">Tecnologías</h2>
                        </div>
                        
                        <p className="text-lg leading-relaxed text-gray-700">
                            Son las TIC´S (Tecnologías de Información y comunicación) la base de interacción 
                            de nuestro sistema pedagógico, en donde a través de un portal que puede consultarse 
                            en Internet en cualquier momento, el alumno puede construir su propio conocimiento, 
                            asistido siempre por profesores altamente capacitados y herramientas tecnológicas 
                            que facilitaran la interacción entre ellos.
                        </p>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default ModeloAcademico;